import { PARTY_ROLE_LABELS, signedVersion } from '@acasa/shared'
import { useState } from 'react'
import type { SectionProps } from './body-utils'
import { SignaturePad } from './signature-pad'
import { photoUrl, useUploadPhoto } from './use-handovers'

/**
 * Signatures.
 *
 * Signing on the device is offered but never required — the PDF prints a
 * signature line for anyone who has not signed, so a protocol can equally be
 * printed and signed on paper. That is why nothing here blocks completion.
 *
 * While a completed protocol is being changed the kind of change decides what
 * happens here. A Kleine Korrektur carries the signatures of the last signed
 * Fassung forward, so they are shown and cannot be touched — re-signing one
 * person would produce a document signed partly for one version and partly
 * for another. A Wesentliche Änderung has had them cleared by the server, and
 * everybody signs again exactly as on a draft.
 */
export function SignaturesSection({
  protocol,
  body,
  patch,
  readOnly: formReadOnly,
  switchToMajor,
}: SectionProps) {
  const roleLabels = PARTY_ROLE_LABELS[protocol.kind]
  const upload = useUploadPhoto(protocol.id)
  const [signing, setSigning] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [switching, setSwitching] = useState(false)

  const amendment = protocol.status === 'amending' ? protocol.amendment : null
  const carriedOver = amendment?.kind === 'minor'
  const readOnly = formReadOnly || carriedOver

  const onSwitch = async () => {
    if (!switchToMajor) return
    setError(null)
    setSwitching(true)
    try {
      await switchToMajor()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Umstellen fehlgeschlagen')
    } finally {
      setSwitching(false)
    }
  }

  const capture = async (partyId: string, blob: Blob) => {
    setError(null)
    try {
      const photo = await upload.mutateAsync({
        file: new File([blob], 'unterschrift.png', { type: 'image/png' }),
        section: 'signature',
        refKey: partyId,
      })

      patch((current) => ({
        ...current,
        signatures: [
          ...current.signatures.filter((entry) => entry.partyId !== partyId),
          { partyId, photoId: photo.id, signedAt: new Date().toISOString() },
        ],
      }))
      setSigning(null)
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : 'Unterschrift konnte nicht gespeichert werden',
      )
    }
  }

  return (
    <div className="space-y-4">
      {carriedOver ? (
        <div className="border-line bg-raised border p-3">
          <p className="text-muted text-xs leading-relaxed">
            Kleine Korrektur: Die Unterschriften der Fassung{' '}
            {signedVersion(protocol.versions)?.version ?? 1} bleiben bestehen und werden im neuen
            PDF ausgewiesen. Sollen alle Beteiligten neu unterschreiben, die Änderung als
            wesentliche Änderung fortsetzen.
          </p>
          {switchToMajor && (
            <button
              type="button"
              disabled={switching}
              onClick={() => void onSwitch()}
              className="border-line text-muted hover:border-brand hover:text-brand mt-3 border px-3 py-2 text-xs transition-colors disabled:opacity-40"
            >
              {switching ? 'wird umgestellt …' : 'Zu wesentlicher Änderung wechseln'}
            </button>
          )}
        </div>
      ) : (
        <>
          {amendment?.kind === 'major' && (
            <p className="text-ink text-xs font-medium">
              Wesentliche Änderung: Alle Beteiligten unterschreiben die neue Fassung.
            </p>
          )}
          <p className="text-muted text-xs">
            Unterschriften können hier direkt auf dem Gerät aufgenommen werden. Wer nicht
            unterschreibt, bekommt im PDF eine Unterschriftszeile zum Ausdrucken — das Protokoll
            lässt sich also auch auf Papier abschließen.
          </p>
        </>
      )}

      {body.parties.map((party) => {
        const signature = body.signatures.find((entry) => entry.partyId === party.id)
        const photo = signature?.photoId
          ? protocol.photos.find((item) => item.id === signature.photoId)
          : undefined

        return (
          <div key={party.id} className="border-line bg-surface border p-4">
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <p className="text-ink text-sm font-medium">{party.name || '— ohne Namen —'}</p>
                <p className="text-muted text-xs">{roleLabels[party.role]}</p>
              </div>
              <span className="text-faint text-[11px]">
                {!party.present
                  ? 'nicht anwesend'
                  : photo
                    ? `unterschrieben ${new Date(signature?.signedAt ?? '').toLocaleDateString('de-DE')}`
                    : 'Unterschrift auf Papier'}
              </span>
            </div>

            {photo ? (
              <div className="flex items-end gap-3">
                <img
                  src={photoUrl(protocol.id, photo.id)}
                  alt={`Unterschrift ${party.name}`}
                  className="border-line bg-paper h-20 max-w-full border object-contain px-2"
                />
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() =>
                      patch((current) => ({
                        ...current,
                        signatures: current.signatures.filter(
                          (entry) => entry.partyId !== party.id,
                        ),
                      }))
                    }
                    className="text-faint hover:text-danger text-xs underline underline-offset-2"
                  >
                    Neu unterschreiben
                  </button>
                )}
              </div>
            ) : signing === party.id ? (
              <SignaturePad
                name={party.name}
                onCancel={() => setSigning(null)}
                onCapture={(blob) => void capture(party.id, blob)}
              />
            ) : (
              !readOnly && (
                <button
                  type="button"
                  onClick={() => setSigning(party.id)}
                  className="border-line text-muted hover:border-brand hover:text-brand border border-dashed px-3 py-2.5 text-xs transition-colors"
                >
                  Jetzt auf dem Gerät unterschreiben
                </button>
              )
            )}
          </div>
        )
      })}

      {error && <p className="text-danger text-xs">{error}</p>}
    </div>
  )
}
